import type { AssignResourceType } from "@/service/base.service";
import type { StudentListType } from "@/types/student.model";

export type AssignmentRecordType = "all" | "class" | "course";

export interface AssignmentResourceRef {
  type?: string;
  grade?: { id: string; name: string };
  class_offering?: { id: string; name: string };
}

export function isAssignmentClassRecord(record: AssignmentResourceRef): boolean {
  return (
    record.type === "class" ||
    Boolean(record.class_offering?.id && !record.grade?.id)
  );
}

export function getAssignmentResourceName(record: AssignmentResourceRef): string {
  return record.grade?.name ?? record.class_offering?.name ?? "";
}

export function getAssignmentResourceId(
  record: AssignmentResourceRef
): string | undefined {
  if (isAssignmentClassRecord(record)) {
    return record.class_offering?.id;
  }

  return record.grade?.id;
}

export function getAssignmentDetailPath(record: AssignmentResourceRef): string | null {
  const resourceId = getAssignmentResourceId(record);

  if (!resourceId) {
    return null;
  }

  if (isAssignmentClassRecord(record)) {
    return `/class-management/detail/${resourceId}`;
  }

  return `/course-management/view-course/${resourceId}`;
}

export function getLessonDropdownQuery(
  resourceType: AssignResourceType,
  resourceId: string
): { search: string; gradeId?: string; classId?: string } {
  if (resourceType === "class") {
    return { search: "", classId: resourceId };
  }

  return { search: "", gradeId: resourceId };
}

export interface AssignmentRecordFilter {
  recordType?: AssignmentRecordType;
  gradeId?: string;
  classId?: string;
}

/**
 * Adds recordType and optional gradeId/classId for assignment/submission APIs.
 * When recordType is omitted, returns {} unless a legacy gradeId/classId is present.
 */
export function buildAssignmentRecordApiParams(
  filter: AssignmentRecordFilter
): Record<string, string> {
  if (!filter.recordType) {
    if (filter.classId?.trim()) {
      return { recordType: "class", classId: filter.classId.trim() };
    }
    if (filter.gradeId?.trim()) {
      return { recordType: "course", gradeId: filter.gradeId.trim() };
    }
    return {};
  }

  if (filter.recordType === "all") {
    return { recordType: "all" };
  }

  if (filter.recordType === "class") {
    const params: Record<string, string> = { recordType: "class" };
    if (filter.classId?.trim()) {
      params.classId = filter.classId.trim();
    }
    return params;
  }

  const params: Record<string, string> = { recordType: "course" };
  if (filter.gradeId?.trim()) {
    params.gradeId = filter.gradeId.trim();
  }
  return params;
}

/** Map StudentTypeSelect value + resource to query fields consumed by services */
export function toRecordFilterQuery(
  listType: StudentListType,
  selectedResourceId?: string
): AssignmentRecordFilter {
  if (listType === "all") {
    return { recordType: "all" };
  }

  if (listType === "class") {
    return selectedResourceId?.trim()
      ? { recordType: "class", classId: selectedResourceId.trim() }
      : { recordType: "class" };
  }

  return selectedResourceId?.trim()
    ? { recordType: "course", gradeId: selectedResourceId.trim() }
    : { recordType: "course" };
}
